export const surveyStatuses = {
  DRAFT: 0,
  ACTIVE: 1,
  SCHEDULED: 2,
  ARCHIVED: 3,
} as const
export type SurveyStatuses = (typeof surveyStatuses)[keyof typeof surveyStatuses]

export const surveyTypes = {
  ONE_TIME: 0,
  RECURRING: 1,
} as const
export type SurveyTypes = (typeof surveyTypes)[keyof typeof surveyTypes]

enum PeriodType {
  DAY = 'D',
  WEEK = 'W',
  MONTH = 'M',
  YEAR = 'Y',
}

export type RecurringSettings = {
  id: number
  period: number
  period_type: PeriodType
}

export const surveyQuestionTypes = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  SINGLE: 'single',
  MULTIPLE: 'multiple',
  LIKERT: 'likert',
  DROPDOWN: 'dropdown',
  MEDIA: 'media',
  DIVIDER: 'divider',
  HEADING: 'heading',
  DESCRIPTION: 'description',
  TITLE: 'title',
  COMPLETION_MESSAGE: 'completion_message',
} as const

export type SurveyQuestionTypes = (typeof surveyQuestionTypes)[keyof typeof surveyQuestionTypes]

export type TextQuestion = {
  id: string
  type: typeof surveyQuestionTypes.TEXT
  name?: string
  translation: Record<string, { title: string }>
  isRequired: true
}

export type TextAreaQuestion = {
  id: string
  type: typeof surveyQuestionTypes.TEXTAREA
  name?: string
  translation: Record<string, { title: string }>
  isRequired: true
}
export type SingleChoiceQuestion = {
  id: string
  type: typeof surveyQuestionTypes.SINGLE
  name?: string
  translation: Record<string, { title: string }>
  isRequired: true
  options: {
    id: string
    value: string
    translation: Record<string, { title: string }>
  }[]
}

export type MultipleChoiceQuestion = {
  id: string
  type: typeof surveyQuestionTypes.MULTIPLE
  name?: string
  translation: Record<string, { title: string }>
  isRequired: true
  options: {
    id: string
    value: string
    translation: Record<string, { title: string }>
  }[]
}

export type LikertScaleQuestion = {
  id: string
  type: typeof surveyQuestionTypes.LIKERT
  name?: string
  translation: Record<string, { title: string; description: string }>
  isRequired: true
  maxValue: number
}

export type DropDownQuestion = {
  id: string
  type: typeof surveyQuestionTypes.DROPDOWN
  name?: string
  translation: Record<string, { title: string }>
  isRequired: true
  options: {
    id: string
    value: string
    translation: Record<string, { title: string }>
  }[]
}

export type MediaBasedQuestion = {
  id: string
  type: typeof surveyQuestionTypes.MEDIA
  name?: string
  isRequired: true
  translation: Record<
    string,
    {
      title: string
      mediaUrl: string
    }
  >
}

export type HeadingQuestion = {
  id: string
  isRequired: true
  type: typeof surveyQuestionTypes.HEADING
  translation: Record<string, { title: string }>
}

export type DescriptionQuestion = {
  id: string
  isRequired: true
  type: typeof surveyQuestionTypes.DESCRIPTION
  translation: Record<string, { title: string }>
}

export type DividerQuestion = {
  id: string
  isRequired: true
  type: typeof surveyQuestionTypes.DIVIDER
}

export type TitleSurvey = {
  id: string
  isRequired: true
  type: typeof surveyQuestionTypes.TITLE
  translation: Record<string, { title: string }>
}

export type CompletionMessageSurvey = {
  id: string
  isRequired: true
  type: typeof surveyQuestionTypes.COMPLETION_MESSAGE
  translation: Record<string, { title: string }>
}

export type SurveyElement =
  | TextQuestion
  | TextAreaQuestion
  | SingleChoiceQuestion
  | MultipleChoiceQuestion
  | LikertScaleQuestion
  | DropDownQuestion
  | MediaBasedQuestion
  | DividerQuestion
  | HeadingQuestion
  | DescriptionQuestion
  | TitleSurvey
  | CompletionMessageSurvey

export type Survey = {
  id: number
  name: string
  status: SurveyStatuses
  type: SurveyTypes
  period?: number
  period_type?: PeriodType
  start_date: string | null
  end_date: string | null
  mandatory: boolean
  recurring_settings?: RecurringSettings
  content: SurveyElement[]
}
